import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { SuggestionDataMap } from 'components/WorkspaceEditor/SuggestionStore'
import { TrackChangeType } from 'models/changes'
import { useCallback } from 'react'
import {
    groupElementsByParent,
    mapSuggestionIdToKey,
} from '../utils/TrackChangesPanelUtils'

interface ProcessedPair {
    addedElement: Partial<TextContentElement>
    deletedElement: Partial<TextContentElement>
    addedElementId: string
    deletedElementId: string
    elementId: string
    afterElementId: string
}

/**
 * Finds pairs of suggestions that can be replaced based on their change types.
 * This function processes the suggestion data and identifies pairs of elements
 * that can be replaced, optimizing the search to reduce unnecessary iterations.
 *
 * @param suggestionData - The map of suggestion data keyed by suggestion ID.
 * @param parentGroups - A map of parent IDs to their child elements.
 * @param suggestionToKeyMap - A map of suggestion IDs to their corresponding keys.
 * @returns An array of processed pairs containing added and deleted elements.
 */
const findReplaceablePairs = (
    suggestionData: Record<string, SuggestionDataMap>,
    parentGroups: Map<
        string,
        Array<{ key: string; element: Partial<TextContentElement> }>
    >,
    suggestionToKeyMap: Map<string, string>
): ProcessedPair[] => {
    const processedPairs: ProcessedPair[] = []
    const processedSuggestions = new Set<string>()

    for (const suggestionId of Object.keys(suggestionData)) {
        if (processedSuggestions.has(suggestionId)) continue

        const matchedKey = suggestionToKeyMap.get(suggestionId)
        if (!matchedKey) continue

        const parentId = matchedKey.split('_')[0]
        const siblings = parentGroups.get(parentId)
        if (!siblings || siblings.length <= 1) continue

        // Find current element in siblings array
        const currentIndex = siblings.findIndex((s) => s.key === matchedKey)
        if (currentIndex === -1 || currentIndex === siblings.length - 1)
            continue

        const currentSibling = siblings[currentIndex]
        const nextSibling = siblings[currentIndex + 1]

        if (!currentSibling?.element || !nextSibling?.element) continue

        const currentChangeType = currentSibling.element.changeType?.toUpperCase()
        const nextChangeType = nextSibling.element.changeType?.toUpperCase()

        // Check for replacement pattern
        const isReplaceChange =
            (currentChangeType === TrackChangeType.ADDED &&
                nextChangeType === TrackChangeType.DELETED) ||
            (currentChangeType === TrackChangeType.DELETED &&
                nextChangeType === TrackChangeType.ADDED)

        if (!isReplaceChange) continue

        const added =
            currentChangeType === TrackChangeType.ADDED
                ? currentSibling.element
                : nextSibling.element
        const deleted =
            currentChangeType === TrackChangeType.DELETED
                ? currentSibling.element
                : nextSibling.element

        const addedElementId = added.id as string
        const deletedElementId = deleted.id as string

        if (!addedElementId || !deletedElementId) continue

        processedPairs.push({
            addedElement: added,
            deletedElement: deleted,
            addedElementId,
            deletedElementId,
            elementId: currentSibling.element.id as string,
            afterElementId: nextSibling.element.id as string,
        })

        // Mark both suggestions as processed to avoid duplicate processing
        processedSuggestions.add(addedElementId)
        processedSuggestions.add(deletedElementId)
    }

    return processedPairs
}

/**
 * Applies the processed pairs to the suggestion data map.
 * @param suggestionData - The map of suggestion data keyed by suggestion ID.
 * @param processedPairs - An array of processed pairs containing added and deleted elements.
 * */
function applyProcessedPairs(
    suggestionData: Record<string, SuggestionDataMap>,
    processedPairs: ProcessedPair[]
) {
    for (const pair of processedPairs) {
        const existingSuggestion = suggestionData[pair.addedElementId]
        if (!existingSuggestion?.suggestions?.[0]) continue

        suggestionData[pair.addedElementId] = {
            ...existingSuggestion,
            id: pair.addedElementId,
            suggestions: [
                {
                    ...existingSuggestion.suggestions[0],
                    id: pair.addedElementId,
                    type: TrackChangeType.REPLACED,
                    attributes: {
                        addedElement: pair.addedElement,
                        deletedElement: pair.deletedElement,
                    },
                    data: {
                        key: `${pair.elementId}_${pair.afterElementId}`,
                        newValue: pair.addedElement.text as string,
                        oldValue: pair.deletedElement.text as string,
                    },
                },
            ],
        }

        // Remove the deleted suggestion
        delete suggestionData[pair.deletedElementId]
    }
}

/**
 * Custom hook to process subsequent suggestions in a track changes context.
 * Optimized to reduce loop iterations and improve performance.
 * @returns {Function} A function that processes suggestions based on the provided data maps and element mappings.
 */
export const useProcessSubsequentSuggestions = () => {
    return useCallback(
        (
            suggestionsDataMap: Record<string, SuggestionDataMap>,
            elementMapping: Record<string, Partial<TextContentElement>>
        ): Record<string, SuggestionDataMap> => {
            // Early return if no data to process
            let hasSuggestions = false
            for (const _ in suggestionsDataMap) {
                hasSuggestions = true
                break
            }
            let hasElements = false
            for (const _ in elementMapping) {
                hasElements = true
                break
            }
            if (!hasSuggestions || !hasElements) {
                return suggestionsDataMap
            }

            const parentGroups = groupElementsByParent(elementMapping)
            const suggestionToKeyMap = mapSuggestionIdToKey(
                elementMapping,
                suggestionsDataMap
            )

            const suggestionData = { ...suggestionsDataMap }

            const processedPairs = findReplaceablePairs(
                suggestionData,
                parentGroups,
                suggestionToKeyMap
            )

            applyProcessedPairs(suggestionData, processedPairs)

            return suggestionData
        },
        []
    )
}
